import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import { Icon } from '~/components/atoms/Icon/Icon'
import ArticleAuthor from '~/components/blocks/ArticleAuthor/ArticleAuthor'
import type { LinkProps } from '~/components/links'
import { resolveLink } from '~/components/serializers'
import { longDateFormat } from '~/utils/formatDate'

import Button, { ButtonVariants } from '../Button/Button'
import type { ImageProps } from '../Image/Image'
import Image from '../Image/Image'
import Link from '../Link/Link'
import type { PDPValuationCtaProps } from '../PDPValuationCta/PDPValuationCta'
import Text, { TextSizes } from '../Text/Text'

import styles from './ArticleListItem.module.css'

export interface ArticleListItemProps {
  id?: string
  title: string
  image: ImageProps
  date: string
  link: LinkProps
  summary?: string
  marginBottom?: boolean
  primaryCategory?: {
    name: string
    slug: string
    categoryOrder: number | string
  }
  secondaryCategories?: {
    name: string
    slug: string
    categoryOrder: number | string
  }[]
  expert: PDPValuationCtaProps['expert'] | null
  showSummary?: boolean
}

export default function ArticleListItem({
  title,
  image,
  date,
  link,
  summary,
  marginBottom = false,
  showSummary = true,
  expert,
}: ArticleListItemProps) {
  const t = useTranslations('articleListItem')
  const resolvedLink = resolveLink(link)

  return (
    <div
      className={classNames(styles.base, marginBottom && styles.marginBottom)}
    >
      <div className={styles.imageContainer}>
        <div className={styles.image}>
          <Link href={resolvedLink} noPrefetch>
            <Image
              {...image}
              width="828"
              height="620"
              sizes="(min-width: 800px) 33vw, 100vw"
              objectFit="cover"
            />
          </Link>
        </div>
      </div>

      <time dateTime={date} className={styles.date}>
        <Text
          size={TextSizes.bodyLrg}
          uppercase={true}
          className={styles.dateText}
        >
          {longDateFormat(date)}
        </Text>
      </time>
      <Text Tag="h3" size={TextSizes.primaryTitleMed} className={styles.title}>
        <Link href={resolvedLink} noPrefetch>
          {title}
        </Link>
      </Text>

      {showSummary && summary && (
        <Text size={TextSizes.bodyMed} className={styles.summary}>
          <Link href={resolvedLink} noPrefetch>
            {summary}
          </Link>
        </Text>
      )}
      {expert && (
        <div className={styles.expert}>
          <ArticleAuthor
            author={{ ...expert, slug: `/${expert.slug}` }}
            preText={t('featuring')}
            showDefault={false}
            centered={false}
          />
        </div>
      )}
      <Button
        className={styles.viewArticle}
        variant={ButtonVariants.textLink}
        iconRight={<Icon type="ArrowRight" />}
        link={resolvedLink}
      >
        {t('viewArticle')}
      </Button>
    </div>
  )
}
